import { InjectionToken } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { WebSocketConnectionState, WebSocketServiceConfig } from './../model/web-socket';

export abstract class WebSocketServiceBase {
  abstract connectionState$: Observable<WebSocketConnectionState>;
  abstract messages$: Observable<any>;

  constructor(_config: WebSocketServiceConfig) {}

  abstract connect(): void;
  abstract send(data: any): void;
  abstract disconnect(): void;
}

export type WSServiceFactory = (...args: ConstructorParameters<typeof WebSocketServiceBase>) => WebSocketServiceBase;
export const WS_SERVICE_FACTORY = new InjectionToken<WSServiceFactory>('WSServiceFactory', {
  factory:
    () =>
    (...args: ConstructorParameters<typeof WebSocketServiceBase>) =>
      new WebSocketService(...args),
});

export class WebSocketService extends WebSocketServiceBase {
  private serviceUrl: string;
  private serviceProtocol: string;
  private messageSubject: Subject<any>;
  private connectionStateSubject: Subject<WebSocketConnectionState>;
  private socket: WebSocketSubject<any> | null = null;
  private connectionState: WebSocketConnectionState = WebSocketConnectionState.Closed;

  public constructor(config: WebSocketServiceConfig) {
    super(config);
    this.serviceUrl = config.url;
    this.serviceProtocol = config.protocol;
    this.messageSubject = new Subject<any>();
    this.connectionStateSubject = new Subject<WebSocketConnectionState>();
  }

  override get connectionState$(): Observable<WebSocketConnectionState> {
    return this.connectionStateSubject.pipe();
  }

  override get messages$(): Observable<any> {
    return this.messageSubject.pipe();
  }

  override connect(): void {
    if (this.connectionState !== WebSocketConnectionState.Closed) {
      return;
    }

    this.setConnectionState(WebSocketConnectionState.Connecting);

    var config: WebSocketSubjectConfig<any> = {
      url: this.serviceUrl,
      protocol: this.serviceProtocol,
      openObserver: {
        next: (param) => {
          this.setConnectionState(WebSocketConnectionState.Opened);
        },
        error: (error) => {
          this.connectionStateSubject.error(error);
          this.setConnectionState(WebSocketConnectionState.Closed);
        },
      },
      closeObserver: {
        next: (param) => {
          this.socket = null;
          this.setConnectionState(WebSocketConnectionState.Closed);
        },
        error: (error) => {
          this.socket = null;
          this.connectionStateSubject.error(error);
        },
      },
      serializer: (message) => {
        return this.serialize(message);
      },
      deserializer: (event) => {
        return this.deserialize(event);
      },
    };
    this.socket = new WebSocketSubject<any>(config);
    this.socket.subscribe(
      (response: any) => {
        if (response instanceof Blob) {
          console.log('Response: BLOB (' + response.size + ' bytes)');
        } else {
          console.log('Response: ' + JSON.stringify(response));
        }

        this.messageSubject.next(response);
      },
      (error: any) => {
        if (
          this.connectionState === WebSocketConnectionState.Connecting ||
          this.connectionState === WebSocketConnectionState.Disconnecting
        ) {
          this.connectionStateSubject.error(error);
          this.setConnectionState(WebSocketConnectionState.Closed);
        } else {
          this.messageSubject.error(error);
        }
      },
    );
  }

  override send(data: any): void {
    if (this.connectionState === WebSocketConnectionState.Opened) {
      if (data instanceof Blob) {
        console.log('Sending BLOB (' + data.size + ' bytes)');
      } else {
        console.log('Sending data: ' + JSON.stringify(data));
      }
      this.socket!.next(data);
    } else {
      this.messageSubject.error('Invalid connection state: ' + this.connectionState);
    }
  }

  override disconnect(): void {
    if (this.connectionState === WebSocketConnectionState.Closed || this.connectionState === WebSocketConnectionState.Disconnecting) {
      this.connectionStateSubject.error('Service connection is closing or closed.');
    } else {
      this.setConnectionState(WebSocketConnectionState.Disconnecting);
      this.socket!.complete();
    }
  }

  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.connectionStateSubject.next(state);
    }
  }

  private serialize(message: any): any {
    if (message instanceof Blob) {
      return message;
    } else {
      return JSON.stringify(message);
    }
  }

  private deserialize(event: MessageEvent) {
    if (event.data instanceof Blob) {
      return event.data;
    } else {
      return JSON.parse(event.data);
    }
  }
}
